import { AuthContext, FireactContext, SetPageTitle } from "../lib/core";
import { Box, Container, Grid, Typography, Stack, IconButton } from "@mui/material";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useSwipeable } from 'react-swipeable';
import React, { useContext, useEffect, useState, useRef } from "react";
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import Button from '@mui/material/Button';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from "react-router-dom";
import Footer from "../template/Footer";
import LandingProductCard from "./LandingProductCard";
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import AiAssistantChat from "./AiAssistantChat";

const landingSteps = [
    'Service Selection',
    'Scheduling',
    'Appointment Address',
    'Overview & Payment'
  ];

const Landing = ({loader}) => {

    const { authInstance, firestoreInstance, functionsInstance } = useContext(AuthContext);
    const { config } = useContext(FireactContext);
    const navigate = useNavigate();
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const { authUser } = useContext(AuthContext);
    const [imageList, setImageList] = useState([]);

    const sampleServices = [
        {
            id: 'hydra-iv',
            name: 'Hydration IV',
            image: 'https://firebasestorage.googleapis.com/v0/b/helical-client-414716.appspot.com/o/webpages-media%2FCopilot_20260102_195108.png?alt=media',
            price: 129,
            rating: 4.8,
            description: 'Rapid rehydration and electrolyte replenishment to restore energy and balance.',
            providers: [{ user: { photoURL: '' } }]
        },
        {
            id: 'vitamin-b',
            name: 'Vitamin Boost',
            image: 'https://firebasestorage.googleapis.com/v0/b/helical-client-414716.appspot.com/o/webpages-media%2FCopilot_20260102_195108.png?alt=media',
            price: 149,
            rating: 4.6,
            description: 'A tailored mix of vitamins to boost immunity and wellbeing.',
            providers: [{ user: { photoURL: '' } }]
        },
        {
            id: 'detox-iv',
            name: 'Detox & Recovery',
            image: 'https://firebasestorage.googleapis.com/v0/b/helical-client-414716.appspot.com/o/webpages-media%2FCopilot_20260102_195108.png?alt=media',
            price: 179,
            rating: 4.7,
            description: 'Support detoxification, recovery and sustained energy.',
            providers: [{ user: { photoURL: '' } }]
        }
        ,
        {
            id: 'myers-cocktail',
            name: "Myers' Cocktail",
            image: 'https://firebasestorage.googleapis.com/v0/b/helical-client-414716.appspot.com/o/webpages-media%2FCopilot_20260102_195108.png?alt=media',
            price: 199,
            rating: 4.9,
            description: 'A powerful blend for overall wellness, energy, and immune support.',
            providers: [{ user: { photoURL: '' } }]
        },
        {
            id: 'immunity-plus',
            name: 'Immunity Plus',
            image: 'https://firebasestorage.googleapis.com/v0/b/helical-client-414716.appspot.com/o/webpages-media%2FCopilot_20260102_195108.png?alt=media',
            price: 159,
            rating: 4.5,
            description: 'Targeted vitamins and antioxidants to help boost immune response.',
            providers: [{ user: { photoURL: '' } }]
        }
    ];

    const [services, setServices] = useState(sampleServices);
    const [servicesLoading, setServicesLoading] = useState(false);

    const currentCircle = useRef(null);
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const itemsPerPage = isMdUp ? 3 : 1;
    const [page, setPage] = useState(0);
    const pages = Math.max(1, Math.ceil(services.length / itemsPerPage));
    const handlers = useSwipeable({
        onSwipedLeft: () => setPage(p => Math.min(p + 1, pages - 1)),
        onSwipedRight: () => setPage(p => Math.max(p - 1, 0)),
        trackMouse: true
    });

    useEffect(() => {
        setPage(p => Math.min(p, pages - 1));
    }, [isMdUp, services.length]);

    // Fetch real treatments from Firestore `treatments` collection
    useEffect(() => {
        let ignore = false;
        const fetchServices = async () => {
            if (!firestoreInstance) return;
            setServicesLoading(true);
            try {
                const q = query(collection(firestoreInstance, 'treatments'), orderBy('name'));
                const querySnapshot = await getDocs(q);
                if (!ignore) {
                    const docs = querySnapshot.docs.map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            name: data.name || doc.id,
                            image: data.image || sampleServices[0].image,
                            price: data.price || 0,
                            rating: data.rating || 0,
                            description: data.description || '',
                            providers: data.providers || [{ user: { photoURL: '' } }]
                        };
                    });
                    if (docs.length > 0) setServices(docs);
                }
            } catch (err) {
                setError('Failed to load treatments.');
            } finally {
                setServicesLoading(false);
            }
        };
        fetchServices();
        return () => { ignore = true; };
    }, [firestoreInstance]);

    useEffect(() => {
        setLoaded(false);
        setError(null);
        // get default permission level name
        let defaultPermission = '';
        for(var permission in config.saas.permissions){
            const value = config.saas.permissions[permission];
            if(value.default){
                defaultPermission = permission;
            }
        }
        
        setLoaded(true);
    },[authInstance, config.saas.permissions, firestoreInstance]);

    return (
        <>
            {loaded?(
                <Container maxWidth={false} disableGutters>
                    <SetPageTitle title="Living Waters" />
                         <Box
                        sx={{
                            backgroundImage: "url('/header.jpg'), linear-gradient(to bottom left, #C5ECE0, #40B5A8)",
                            backgroundPosition: 'top center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'cover',
                            maxHeight: '285px',
                            height: '285px',
                            width: '100%',
                            color: '#042027'
                            }}
                        >
                        <Stack spacing={0} justifyContent="center" alignItems="center" sx={{height: '100%'}}>
                            
                            <Typography component="div" variant="h3" align="center" sx={{p:2, fontSize: {xs: '30px', md: '40px'}}}>
                            IV Nutritional Therapy for Living Abundantly
                            </Typography>
                            <Typography component="div" variant="h4" align="center" sx={{pt:0,pb:2, fontSize: {xs: '20px', md: '30px'}}}>
                            A modern, trusted platform connecting licensed nurses with customers seeking safe, convenient IV therapy services - anytime, anywhere.
                            </Typography>
                            
                        </Stack>
                     </Box>
                     <Box sx={{ flexGrow: 1, p: 2, width: '100%', minHeight: '100vh'}} >
                     <Grid container spacing={0}>
                           <Grid xs={12} md={4} item={true}>
                           <Stack spacing={0} justifyContent="center" alignItems="center" sx={{height: '100%', width: '100%'}}>
                                <img src="https://firebasestorage.googleapis.com/v0/b/helical-client-414716.appspot.com/o/webpages-media%2FCopilot_20260102_195108.png?alt=media&token=d88efbc4-1387-46d5-9fa3-8fa843358bea" style={{maxWidth: '400px', width: '100%', borderRadius: '10px'}}/>
                           </Stack>
                           </Grid>
                           <Grid xs={12} md={8} item={true}>
                           <Stack spacing={0} justifyContent="center" alignItems="center" sx={{height: '100%', width: '100%'}}>
                            <Typography component="div" variant="h5" align="center" sx={{pt:2}}>
                                Recover Fast, Boost Energy, & Promote Wellness with a Mobile IV Service.
                                </Typography>
                                <Typography component="div" align="left" sx={{pb:0}}>
                                    <ul>
                                        <li>
                                        Mobile IV therapy at your home, hotel or office
                                        </li>
                                        <li>
                                        Variety of services for wellness and recovery
                                        </li>
                                        <li>
                                        Personalized IV therapy with vitamins tailored to individual needs, ensuring each IV is uniquely crafted for you
                                        </li>
                                        <li>
                                        Treatments are provided by licensed nurses.
                                        </li>
                                    </ul>
                                </Typography>
                            </Stack>
                           </Grid>
                           <Grid xs={12} md={12} item={true}>
                                <Typography component="div" variant="h5" align="center" sx={{pt:2}}>
                                    Popular Treatments
                                </Typography>
                                <Box {...handlers} sx={{ py: 2, px: 2, height: { xs: 430, md: 430 }, overflow: 'hidden', position: 'relative' }}>
                                    {pages > 1 && (
                                        <IconButton
                                            onClick={() => setPage(p => Math.max(p - 1, 0))}
                                            sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                                        >
                                            <ArrowBackIosNewIcon />
                                        </IconButton>
                                    )}
                                    {pages > 1 && (
                                        <IconButton
                                            onClick={() => setPage(p => Math.min(p + 1, pages - 1))}
                                            sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                                        >
                                            <ArrowForwardIosIcon />
                                        </IconButton>
                                    )}
                                    <Grid container spacing={2}>
                                        {services.slice(page * itemsPerPage, page * itemsPerPage + itemsPerPage).map((service) => (
                                            <Grid item xs={12} md={4} key={service.id}>
                                                <LandingProductCard service={service} showDetails={(s) => navigate(`/booking?service=${s.id}`)} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                                    {Array.from({ length: pages }).map((_, i) => (
                                        <Box
                                            key={i}
                                            onClick={() => setPage(i)}
                                            sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: i === page ? '#042027' : '#ccc', mx: 0.5, cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                           </Grid>
                           {!(authUser.user && authUser.userType === "Nurse") && (
                           <Grid xs={12} md={12} item={true}>
                                <Typography component="div" align="center" sx={{pt:{xs: 2, md: 5}, pb:0}}>
                                <Stack spacing={0} justifyContent="center" alignItems="center">
                                    <Stepper activeStep={0} alternativeLabel sx={{width: {xs: '100%', md: '70%'}}}>
                                        {landingSteps.map((label) => (
                                        <Step key={label}>
                                            <StepLabel>{label}</StepLabel>
                                        </Step>
                                        ))}
                                    </Stepper>
                                </Stack>
                                </Typography>
                                <Typography component="div" align="center" sx={{pt:3, pb:1}}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<EventAvailableIcon />}
                                        onClick={() => navigate('/booking')}
                                    >
                                        Book Now
                                    </Button>
                                </Typography>
                           </Grid>
                           )}
                           <Grid xs={12} md={12} item={true}>
                                <Grid container spacing={0}>
                                    <Grid xs={12} md={12} item={true}>
                                        <Typography component="div" variant="h5" align="center" sx={{p:2}}>
                                        What customers say
                                        </Typography>
                                    </Grid>
                                    <Grid xs={12} md={4} item={true}>
                                        <Stack spacing={0} justifyContent="center" alignItems="center">
                                            <Typography component="div" align="center" sx={{p:2}}>
                                                <img src='https://firebasestorage.googleapis.com/v0/b/helical-client-414716.appspot.com/o/webpages-media%2FBCO.03780e59-57bc-4301-9aeb-79132156364a-modified.png?alt=media&token=7bead3ba-a00b-489e-a7cf-00c9f66ca56c' width='50%'/>
                                            </Typography>
                                            <Typography component="div" align="center" sx={{p:2}}>
                                            Hana Lee - "As a nurse, I appreciate how it supports both patients and caregivers with dignity. The tone is gentle, the navigation is intuitive, and the messaging is deeply respectful."
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid xs={12} md={4} item={true}>
                                        <Stack spacing={0} justifyContent="center" alignItems="center">
                                            <Typography component="div" align="center" sx={{p:2}}>
                                                <img src='https://firebasestorage.googleapis.com/v0/b/helical-client-414716.appspot.com/o/webpages-media%2FBCO.c59310c9-ac81-429d-ad85-f6401fb48e4e-modified.png?alt=media&token=58dad280-a59e-441b-9e0c-ff821c071011' width='50%'/>
                                            </Typography>
                                            <Typography component="div" align="center" sx={{p:2}}>
                                            Julian Park - "Great treatment! Quick appointment through App. Technician was on time. Will book again!"
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid xs={12} md={4} item={true}>
                                        <Stack spacing={0} justifyContent="center" alignItems="center">
                                            <Typography component="div" align="center" sx={{p:2}}>
                                                <img src='https://firebasestorage.googleapis.com/v0/b/helical-client-414716.appspot.com/o/webpages-media%2FBCO.90ab4367-c982-43c2-bba2-306523253cf5-modified.png?alt=media&token=b2f633e6-3bb5-46f8-a93d-db78eb8952c7' width='50%'/>
                                            </Typography>
                                            <Typography component="div" align="center" sx={{p:2}}>
                                            Brooke Jensen - "Straightforward, empowering, and no fluff. Livingwaters.biz  feels like it's built for real people with real needs. I respect that."
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                </Grid>
                           </Grid>
                     </Grid>
                    </Box>
                    <Footer />
                    <AiAssistantChat />
                </Container>
            ):(
                <>{loader}</>
            )}
        </>
    )
}

export default Landing;